import { useEffect, useState, type Dispatch } from "react"

import { Checkbox } from "@/components/ui/checkbox"
import { Label } from "@/components/ui/label"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { Textarea } from "@/components/ui/textarea"
import {
  useCallToActions,
  useIdentities,
} from "@/api/hooks/campaignForm"

import { AdTextsSmart } from "../widgets/AdTextsSmart"
import { CarouselBatches } from "../widgets/CarouselBatches"
import { CarouselPreviewModal } from "../widgets/CarouselPreviewModal"
import { CtaPicker } from "../widgets/CtaPicker"
import { IdentityPicker } from "../widgets/IdentityPicker"
import { LandingUrlRows } from "../widgets/LandingUrlRows"
import { VideoUploader } from "../widgets/VideoUploader"
import type { CarouselBatch, FormAction, FormState } from "../state/formReducer"

/**
 * Step 3 — Ads. Parity: `form.html:1584-1688` + every creatives/cta
 * module in the source.
 */

interface Props {
  state: FormState
  dispatch: Dispatch<FormAction>
  adTextError?: string
  smartAdTextsError?: string
  ctaError?: string
  campaignUrlError?: string
  creativesError?: string
  carouselError?: string
}

export function AdsStep({
  state,
  dispatch,
  adTextError,
  smartAdTextsError,
  ctaError,
  campaignUrlError,
  creativesError,
  carouselError,
}: Props) {
  const advertiserId = state.advertiserId ?? ""
  const identitiesQuery = useIdentities(state.advertiserId)
  const ctasQuery = useCallToActions(state.advertiserId, state.objectiveType)

  const [previewBatch, setPreviewBatch] = useState<CarouselBatch | null>(null)

  const identities = identitiesQuery.data?.identities
  const canUseCustomIdentity =
    identitiesQuery.data?.can_use_custom_identity !== false

  // Auto-select identity when exactly one is returned. Parity:
  // `adgroup.js:316`.
  useEffect(() => {
    const list = identities
    if (!list || list.length !== 1) return
    if (state.unifiedIdentityId) return
    const only = list[0]
    dispatch({
      type: "SET_IDENTITY",
      id: only.id,
      identity_type: only.identity_type,
      bc_id: only.identity_authorized_bc_id ?? "",
    })
  }, [identities, state.unifiedIdentityId, dispatch])

  const hideCarousels =
    state.creativeMode === "SMART_CREATIVE" && !state.isSmartPlusCampaign

  return (
    <section className="rounded-lg border border-[#4a4a4a] bg-[#2d2d2d] p-4 shadow-[inset_0_1px_0_rgba(255,255,255,0.03)]">
      <h3 className="mb-3 text-lg font-semibold">✨ Step 3: Create Ads</h3>

      {/* Creative mode / Smart+ auto-add */}
      {state.isSmartPlusCampaign ? (
        <div className="rounded-lg border border-[#4a4a4a] bg-[#262626] p-3">
          <label className="flex items-center gap-2">
            <Checkbox
              checked={state.creativeAutoAddToggle}
              onCheckedChange={(value) =>
                dispatch({
                  type: "SET_CREATIVE_AUTO_ADD_TOGGLE",
                  value: value === true,
                })
              }
            />
            <span className="text-sm font-semibold">
              Add AI Generated Content
            </span>
          </label>
        </div>
      ) : (
        <div className="rounded-lg border border-[#4a4a4a] bg-[#262626] p-3">
          <Label>Creative Mode</Label>
          <RadioGroup
            value={state.creativeMode}
            onValueChange={(value) =>
              dispatch({
                type: "SET_CREATIVE_MODE",
                value: value as "MANUAL" | "SMART_CREATIVE",
              })
            }
            className="mt-2 flex flex-col gap-2 sm:flex-row sm:gap-6"
          >
            <label className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="MANUAL" />
              Manual Creative
            </label>
            <label className="flex items-center gap-2 text-sm">
              <RadioGroupItem value="SMART_CREATIVE" />
              Smart Creative
            </label>
          </RadioGroup>
          <p className="mt-2 text-xs text-white">
            {state.creativeMode === "MANUAL"
              ? "Create individual ads for each video/carousel with shared settings."
              : "Upload multiple videos and ad text variations. TikTok will automatically combine and optimize them."}
          </p>
        </div>
      )}

      {/* Shared settings */}
      <div className="mt-4 rounded-lg border border-[#4a4a4a] bg-[#262626] p-3">
        <h4 className="mb-3 font-semibold">📋 Shared Settings (All Ads)</h4>

        <Label>TikTok Identity:</Label>
        <IdentityPicker
          advertiserId={advertiserId}
          identities={identities}
          canUseCustomIdentity={canUseCustomIdentity}
          isLoading={identitiesQuery.isLoading}
          isError={identitiesQuery.isError}
          selectedId={state.unifiedIdentityId}
          onSelect={(id, identity_type, bc_id) =>
            dispatch({ type: "SET_IDENTITY", id, identity_type, bc_id })
          }
        />

        {state.creativeMode === "MANUAL" ? (
          <div className="mt-3" data-form-field="adText">
            <Label>Ad Text:</Label>
            <Textarea
              rows={3}
              maxLength={100}
              required
              value={state.unifiedAdText}
              className={adTextError ? "border-red-500 focus-visible:ring-red-500" : ""}
              onChange={(e) =>
                dispatch({ type: "SET_UNIFIED_AD_TEXT", value: e.target.value })
              }
            />
            <p className={`mt-1 text-xs ${adTextError ? "text-red-400" : "text-white"}`}>
              {adTextError ?? "Maximum 100 characters"}
            </p>
          </div>
        ) : (
          <div className="mt-3" data-form-field="smartAdTexts">
            <AdTextsSmart
              texts={state.smartAdTexts}
              onAdd={() => dispatch({ type: "ADD_SMART_AD_TEXT" })}
              onRemove={(id) => dispatch({ type: "REMOVE_SMART_AD_TEXT", id })}
              onUpdate={(id, text) =>
                dispatch({ type: "UPDATE_SMART_AD_TEXT", id, text })
              }
            />
            {smartAdTextsError ? (
              <p className="mt-1 text-xs text-red-400">{smartAdTextsError}</p>
            ) : null}
          </div>
        )}

        <div
          className={`mt-3 ${ctaError ? "rounded-md border border-red-500/80 p-2" : ""}`}
          data-form-field="ctas"
        >
          <Label>Call to Action:</Label>
          <CtaPicker
            ctas={ctasQuery.data}
            selected={state.selectedCtas}
            onToggle={(cta) => dispatch({ type: "TOGGLE_CTA", cta })}
          />
          {ctaError ? <p className="mt-1 text-xs text-red-400">{ctaError}</p> : null}
        </div>

        <div
          className={`mt-3 ${campaignUrlError ? "rounded-md border border-red-500/80 p-2" : ""}`}
          data-form-field="campaignUrls"
        >
          <Label>Landing Page URL(s):</Label>
          <LandingUrlRows
            campaigns={state.campaigns}
            onUrlChange={(id, url) =>
              dispatch({ type: "UPDATE_CAMPAIGN_URL", id, url })
            }
          />
          {campaignUrlError ? (
            <p className="mt-1 text-xs text-red-400">{campaignUrlError}</p>
          ) : null}
        </div>
      </div>

      {/* Make ads unique — CreOS uniquify before TikTok launch */}
      <div className="mt-4 rounded-lg border border-[#4a4a4a] bg-[#262626] p-3">
        <label className="flex items-center gap-2">
          <Checkbox
            checked={state.makeAdsUnique}
            onCheckedChange={(value) =>
              dispatch({
                type: "SET_MAKE_ADS_UNIQUE",
                value: value === true,
              })
            }
          />
          <span className="text-sm font-semibold">Make ads unique</span>
        </label>
      </div>

      {/* Videos */}
      <div
        data-form-field="creatives"
        className={`mt-4 rounded-lg bg-[#262626] p-3 ${
          creativesError ? "border border-red-500/80" : "border border-[#4a4a4a]"
        }`}
      >
        <h4 className="mb-2 font-semibold">🎬 Upload Videos</h4>
        <VideoUploader
          files={state.videoFiles}
          onAdd={(files, covers) =>
            dispatch({ type: "ADD_VIDEOS", files, covers })
          }
          onRemove={(index) => dispatch({ type: "REMOVE_VIDEO", index })}
        />
        {creativesError ? (
          <p className="mt-1 text-xs text-red-400">{creativesError}</p>
        ) : null}
      </div>

      {/* Carousels */}
      {!hideCarousels ? (
        <div
          data-form-field="carousel"
          className={`mt-4 rounded-lg bg-[#262626] p-3 ${
            carouselError ? "border border-red-500/80" : "border border-[#4a4a4a]"
          }`}
        >
          <h4 className="mb-2 font-semibold">🎠 Upload Carousels</h4>
          <CarouselBatches
            batches={state.carouselBatches}
            onAddBatch={() => dispatch({ type: "ADD_CAROUSEL_BATCH" })}
            onRemoveBatch={(id) =>
              dispatch({ type: "REMOVE_CAROUSEL_BATCH", id })
            }
            onDuplicateBatch={(id) =>
              dispatch({ type: "DUPLICATE_CAROUSEL_BATCH", id })
            }
            onUpdateImages={(id, images) =>
              dispatch({ type: "UPDATE_CAROUSEL_IMAGES", id, images })
            }
            onUpdateMusic={(id, music) =>
              dispatch({ type: "UPDATE_CAROUSEL_MUSIC", id, music })
            }
            onReorderImages={(id, from, to) =>
              dispatch({ type: "REORDER_CAROUSEL_IMAGES", id, from, to })
            }
            onPreview={(batch) => setPreviewBatch(batch)}
          />
          {carouselError ? (
            <p className="mt-1 text-xs text-red-400">{carouselError}</p>
          ) : null}
        </div>
      ) : null}

      <CarouselPreviewModal
        batch={previewBatch}
        onClose={() => setPreviewBatch(null)}
      />
    </section>
  )
}
